export type AlertStatus = "new" | "acknowledged" | "attended" | "on_hold" | "completed"

export interface UpdateAlertInput {
  name?: string
  severity?: "critical" | "warning" | "info"
  status?: AlertStatus
  note?: string
  /** Set to `null` to clear assignee; omit to leave unchanged. */
  assignedToUserId?: string | null
  /** Admin-only: update alert metadata */
  metadata?: Record<string, unknown> | null
}

/** Time range for querying and displaying data */
export interface AlertDataTimeRange {
  start: string
  end: string
}

/** A time range to highlight on charts (e.g. when the alert condition was detected) */
export interface AlertHighlightRange {
  start: string
  end: string
  label?: string
}

/** Graph specification for alert data visualization */
export interface AlertGraphSpec {
  /** Optional explicit graph ID. If omitted, will be generated from field/role/class using getAlertGraphId() */
  graph_id?: string
  title: string
  field: string
  role?: string | null
  class?: string | null
  unit?: string
  source_unit?: string
  annotation?: string
  /** Use stacked bar chart instead of line chart (for power generation, load, etc.) */
  use_stacked_bar?: boolean
  /** Use battery view (discharge/charge bars + SOC line). Requires class="battery" and field="power" */
  battery_view?: boolean
  /** Overlay graph IDs to fetch and display alongside the main graph */
  overlay_graph_ids?: string[]
  /** Aggregation role for the query (e.g., "load" for consumer power aggregation) */
  aggregation_role?: string
  /** Include connection-level data in addition to asset-level data */
  include_connections?: boolean
  /** Query only connection-level data, exclude asset-level data */
  connections_only?: boolean
  /** When set (1, 2, or 3), the specified phase is visually emphasised and others are dimmed in the phase balance chart */
  highlight_phase?: 1 | 2 | 3
}

/**
 * Generate a stable graph ID for Timescale batch queries.
 * Matches the logic in systems/src/utils/timescaleBatchGraph.ts
 */
export function getAlertGraphId(spec: AlertGraphSpec): string {
  const { field, role, class: assetClass } = spec
  if (role) {
    return `${field}__role_${role}`
  }
  if (assetClass) {
    return `${field}__class_${assetClass}`
  }
  return field
}

/** Data context stored in alert.metadata for "See Data" popover */
export interface AlertDataContext {
  system_id: string
  time_range: AlertDataTimeRange
  highlight_ranges?: AlertHighlightRange[]
  graphs: AlertGraphSpec[]
}

/** Alert metadata structure */
export interface AlertMetadata {
  data_context?: AlertDataContext
  [key: string]: unknown
}

/** Helper to check if alert has data context for "See Data" button */
export function hasAlertDataContext(
  metadata: AlertMetadata | null | undefined
): metadata is AlertMetadata & { data_context: AlertDataContext } {
  return (
    metadata != null &&
    typeof metadata === "object" &&
    "data_context" in metadata &&
    metadata.data_context != null &&
    typeof metadata.data_context === "object" &&
    "system_id" in metadata.data_context &&
    "time_range" in metadata.data_context &&
    "graphs" in metadata.data_context &&
    Array.isArray(metadata.data_context.graphs) &&
    metadata.data_context.graphs.length > 0
  )
}
